import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import CreatePricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/CreatePricingPlanVersionFormModal';
import CellPricingPlanVersionActions from 'src/features/pricingPlan/components/table/CellPricingPlanVersionActions';
import VersionStatusBadge from 'src/features/pricingPlan/components/VersionStatusBadge';
import {
    pricingBillingBadgeOptions,
    pricingCalculationBadgeOptions,
    pricingStatusBadgeOptions,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { CellBadge, CellDatetime } from 'src/registry/components/table';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<PricingPlan>();

function PricingPlanVersions() {
    const { formatMessage, locale } = useIntl();

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { planId = '', identity = '' } = useParams();
    const { parkingId } = useParking();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
            identity,
            parkingId,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions, identity, parkingId],
    );

    const { data: planData } = useGetPricingPlan({ planId, parkingId });

    const planName =
        planData?.translations?.find((plan) => plan.language === locale)?.name ??
        formatMessage(pricingPlanMessages.pricingPlan);

    const { data, isLoading, isError } = useListPricingPlans(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.pricingPlanVersionControl));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: (ctx) => {
                const name = ctx.row.original.translations.find((translation) => translation.language === locale)?.name;
                return (
                    <div className="flex flex-col items-start">
                        <CellLink
                            to={`/${PATH.ADMINISTRATION}/pricing-plan/${ctx.row.original.id}`}
                            render={() => name}
                            {...ctx}
                        />
                        <VersionStatusBadge
                            isCurrentVersion={ctx.row.original.is_current_version}
                            validFrom={ctx.row.original.valid_from}
                        />
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('billing_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingBillingBadgeOptions,
                title: formatMessage(pricingPlanMessages.billingType),
            },
        }),
        columnHelper.accessor('calculation_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingCalculationBadgeOptions,
                title: formatMessage(pricingPlanMessages.calculationType),
            },
        }),
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(pricingPlanMessages.versionNumber),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeTo),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: pricingStatusBadgeOptions,
                title: formatMessage(pricingPlanMessages.status),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPricingPlanVersionActions,
            maxSize: 45,
        }),
    ];

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/pricing-plan`} />
            <SectionBlock>
                <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                    <SectionBlockHead>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{planName}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            <VersionStatusBadge
                                isCurrentVersion={planData?.is_current_version}
                                validFrom={planData?.valid_from}
                            />
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
            </SectionBlock>
            <DataTable<PricingPlan, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={data}
                search={search}
                filters={filters}
                sorting={sorting}
                pagination={pagination}
                semantics={{
                    title: formatMessage(pricingPlanMessages.pricingPlanVersions),
                    entryName: formatMessage(pricingPlanMessages.version).toLowerCase(),
                    entryNames: formatMessage(pricingPlanMessages.versions).toLowerCase(),
                    placeholder: formatMessage(pricingPlanMessages.pricingPlanVersion),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    ...(managePricingPlans && { newEntry: CreatePricingPlanVersionFormModal }),
                }}
            />
        </>
    );
}

export default PricingPlanVersions;
